import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Copy, ExternalLink, Film, Play, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";

const RumblePage = () => {
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    toast.success("Link copiado para a área de transferência!");
    setTimeout(() => setCopied(null), 2000);
  };

  const canais = [
    {
      nome: "Striderman",
      link: "https://rumble.com/c/c-1285082?e9s=src_v1_cbl",
      descricao: "Canal principal com conteúdos variados"
    },
    {
      nome: "Rockdragon",
      link: "https://rumble.com/c/Rockdragon",
      descricao: "Canal dedicado à música rock e cultura alternativa"
    },
    {
      nome: "Mestre do Jogo do Bicho",
      link: "https://rumble.com/c/c-656703",
      descricao: "Dicas e análises sobre o jogo do bicho"
    },
    {
      nome: "Freebitcoin Ultimate",
      link: "https://rumble.com/c/c-678967",
      descricao: "Estratégias para ganhar bitcoin gratuitamente"
    },
    {
      nome: "Sussurros da Meia Noite",
      link: "https://rumble.com/c/c-7689103",
      descricao: "Histórias de terror e mistério"
    },
    {
      nome: "Ganhe Dinheiro",
      link: "https://rumble.com/c/c-1223953",
      descricao: "Dicas para ganhar dinheiro online"
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent py-2">
            Rumble - Plataforma de Vídeos Alternativa
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Rumble é uma plataforma de vídeos que valoriza a liberdade de expressão e oferece monetização justa para criadores de conteúdo.
          </p>
        </div>

        <div className="mb-12">
          <Card className="overflow-hidden border border-border">
            <CardHeader className="bg-muted/50">
              <CardTitle className="text-xl">Crie sua Conta no Rumble</CardTitle>
              <CardDescription>
                Use o link de referência para se registrar na plataforma
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex flex-col gap-4">
                <p className="text-muted-foreground">
                  Registre-se no Rumble para acessar conteúdos exclusivos e apoiar criadores independentes. Ao usar o link de referência, você ajuda a manter nossos canais ativos.
                </p>
                <div className="flex flex-wrap gap-4 items-center justify-between">
                  <Button 
                    asChild 
                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    <a 
                      href="https://rumble.com/register/FARIAS/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Criar Conta no Rumble
                    </a>
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => handleCopy("https://rumble.com/register/FARIAS/")}
                    className={copied === "https://rumble.com/register/FARIAS/" ? "bg-primary/10" : ""}
                  >
                    <Copy size={16} className="mr-1" /> 
                    {copied === "https://rumble.com/register/FARIAS/" ? "Copiado!" : "Copiar Link"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Meus Canais no Rumble</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {canais.map((canal, index) => (
              <Card key={index} className="overflow-hidden border border-border h-full">
                <CardHeader className="bg-muted/50">
                  <CardTitle className="text-xl flex items-center">
                    <Film className="mr-2 h-5 w-5 text-primary" /> {canal.nome}
                  </CardTitle>
                  <CardDescription>{canal.descricao}</CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <a 
                      href={canal.link} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="flex items-center text-primary hover:underline"
                    >
                      <Play size={16} className="mr-1" /> Visitar Canal
                    </a>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleCopy(canal.link)}
                      className={copied === canal.link ? "bg-primary/10" : ""}
                    >
                      <Share2 size={16} className="mr-1" /> 
                      {copied === canal.link ? "Copiado!" : "Compartilhar"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div className="bg-muted p-6 rounded-lg mb-12">
          <h2 className="text-2xl font-semibold mb-4">Vantagens do Rumble</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-card p-4 rounded-lg border border-border">
              <h3 className="text-lg font-medium mb-2">Liberdade de Expressão</h3>
              <p className="text-sm text-muted-foreground">O Rumble valoriza a liberdade de expressão e oferece menos censura para criadores de conteúdo.</p>
            </div>
            <div className="bg-card p-4 rounded-lg border border-border">
              <h3 className="text-lg font-medium mb-2">Monetização Justa</h3>
              <p className="text-sm text-muted-foreground">Criadores recebem uma parcela maior dos ganhos de publicidade comparado a outras plataformas.</p>
            </div>
            <div className="bg-card p-4 rounded-lg border border-border">
              <h3 className="text-lg font-medium mb-2">Conteúdo Alternativo</h3>
              <p className="text-sm text-muted-foreground">Acesse vídeos e informações que podem ser censurados em outras plataformas de streaming.</p>
            </div>
          </div>
        </div>

        <div className="text-center">
          <Button 
            asChild 
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <a 
              href="https://rumble.com/c/c-1285082?e9s=src_v1_cbl" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              Acesse Meu Canal Principal
            </a>
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default RumblePage;